import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  FormControl,
  InputLabel,
  FormHelperText,
  MenuItem,
  Select,
  Checkbox,
  ListItemText,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { useMutation } from 'react-query';
import { createRajivLead } from '../../api/services';
import CustomInput from '../../Components/Inputs/CustomInput';
import ArrowDropDown from '@mui/icons-material/ArrowDropDown';

export interface RajivFormBase {
  name: string;
  email: string;
  phoneNumber: string;
  serviceType: 'Sales' | 'Branding' | 'Mentoring';
  sourcePerson: 'rajiv';
}

export interface RajivSalesForm extends RajivFormBase {
  serviceType: 'Sales';
  propertyInterested: string;
  budgetRange: string;
  locationPreference: string;
  timelineToBuy: 'Immediately' | '1-3 Months' | '3-6 Months' | '6+ Months';
  message?: string;
}

export interface RajivBrandingForm extends RajivFormBase {
  serviceType: 'Branding';
  businessName: string;
  industry: string;
  currentChallenges: string;
  lookingFor: string[];
  message?: string;
}

export interface RajivMentoringForm extends RajivFormBase {
  serviceType: 'Mentoring';
  profession: string;
  goalOfMentorship: string;
  preferredMode: 'Call' | 'Video' | 'In-person';
  availability: 'Weekdays' | 'Weekends' | 'Evenings';
  message?: string;
}

export type RajivFormType = RajivSalesForm | RajivBrandingForm | RajivMentoringForm;

const RajivForm: React.FC = () => {
  const { control, handleSubmit, watch, reset } = useForm<RajivFormType>({
    defaultValues: {
      name: '',
      email: '',
      phoneNumber: '',
      serviceType: 'Sales',
      sourcePerson: 'rajiv',
    } as RajivFormType,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const serviceType = watch('serviceType');

  const { mutate, isLoading } = useMutation(createRajivLead, {
    onSuccess: () => {
      alert('Lead created successfully!');
      reset();
    },
    onError: () => {
      alert('Failed to create lead. Please try again.');
    },
  });

  const validate = (data: RajivFormType) => {
    const newErrors: Record<string, string> = {};

    // Basic validations
    if (!data.name.trim()) newErrors.name = 'Name is required';
    if (!data.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email))
      newErrors.email = 'Invalid email';
    if (!data.phoneNumber.trim() || !/^\d{10}$/.test(data.phoneNumber))
      newErrors.phoneNumber = 'Phone number must be exactly 10 digits';

    // Service-specific validations
    if (data.serviceType === 'Sales') {
      if (!data.propertyInterested?.trim())
        newErrors.propertyInterested = 'Property Interested is required';
      if (!data.budgetRange?.trim())
        newErrors.budgetRange = 'Budget Range is required';
      if (!data.locationPreference?.trim())
        newErrors.locationPreference = 'Location Preference is required';
      if (!data.timelineToBuy?.trim())
        newErrors.timelineToBuy = 'Timeline to Buy is required';
    } else if (data.serviceType === 'Branding') {
      if (!data.businessName?.trim())
        newErrors.businessName = 'Business Name is required';
      if (!data.industry?.trim()) newErrors.industry = 'Industry is required';
      if (!data.currentChallenges?.trim())
        newErrors.currentChallenges = 'Current Challenges are required';
      if (!data.lookingFor || data.lookingFor.length === 0)
        newErrors.lookingFor = 'Select at least one option';
    } else if (data.serviceType === 'Mentoring') {
      if (!data.profession?.trim())
        newErrors.profession = 'Profession is required';
      if (!data.goalOfMentorship?.trim())
        newErrors.goalOfMentorship = 'Goal of Mentorship is required';
      if (!data.preferredMode?.trim())
        newErrors.preferredMode = 'Preferred Mode is required';
      if (!data.availability?.trim())
        newErrors.availability = 'Availability is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const onSubmit = (data: RajivFormType) => {
    if (validate(data)) {
      mutate(data);
    }
  };

  const getButtonText = () => {
    switch (serviceType) {
      case 'Sales':
        return 'Submit Inquiry';
      case 'Branding':
        return 'Submit Branding Request';
      case 'Mentoring':
        return 'Book a Mentorship';
      default:
        return 'Submit';
    }
  };

  return (
    <Box
      sx={{
        maxWidth: '600px',
        margin: '0 auto',
        padding: '20px',
        boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
        borderRadius: '8px',
        backgroundColor: '#fff',
      }}
    >
      {/* Banner */}
      <Box
        sx={{
          backgroundColor: '#1976d2',
          color: '#fff',
          padding: '16px',
          borderRadius: '8px 8px 0 0',
          textAlign: 'center',
        }}
      >
        <Typography variant="h5" fontWeight="bold">
          Rajiv Williams Dynamic Form
        </Typography>
        <Typography variant="body2">Please fill out the form below</Typography>
      </Box>

      {/* Form */}
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box sx={{ padding: '16px' }}>
          {/* Name */}
          <FormControl fullWidth sx={{ marginBottom: 2 }}>
            <InputLabel  shrink sx={{ ml: -1 }}>Name</InputLabel>
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <CustomInput
                  placeholder="Enter name..."
                  {...field}
                  error={!!errors.name}
                />
              )}
            />
            <FormHelperText error>{errors.name}</FormHelperText>
          </FormControl>

          {/* Email */}
          <FormControl fullWidth sx={{ marginBottom: 2 }}>
            <InputLabel  shrink sx={{ ml: -1 }}>Email</InputLabel>
            <Controller
              name="email"
              control={control}
              render={({ field }) => (
                <CustomInput
                  placeholder="Enter email..."
                  {...field}
                  error={!!errors.email}
                />
              )}
            />
            <FormHelperText error>{errors.email}</FormHelperText>
          </FormControl>

          {/* Phone Number */}
          <FormControl fullWidth sx={{ marginBottom: 3.4 }}>
            <InputLabel  shrink sx={{ ml: -1 }}>Phone Number</InputLabel>
            <Controller
              name="phoneNumber"
              control={control}
              render={({ field }) => (
                <CustomInput
                  placeholder="Enter phone number..."
                  {...field}
                  error={!!errors.phoneNumber}
                />
              )}
            />
            <FormHelperText error>{errors.phoneNumber}</FormHelperText>
          </FormControl>

          {/* Service Type */}
          <FormControl fullWidth sx={{ marginBottom: 2 }}>
            <InputLabel shrink sx={{ ml: -1, mt: -1.6 }} htmlFor="service-type-input">
              Service Type
            </InputLabel>
            <Controller
              name="serviceType"
              control={control}
              render={({ field }) => (
                <Select
                  id="service-type-input"
                  {...field}
                  error={!!errors.serviceType}
                  IconComponent={ArrowDropDown}
                  renderValue={(selected) =>
                    selected ? (
                      <Box display="flex" alignItems="center">
                        {selected}
                      </Box>
                    ) : (
                      'Select Service Type'
                    )
                  }
                  style={{
                    width: "100%",
                    height: "40px",
                    border: "1px solid #1212121A",
                    borderRadius: "10px",
                    opacity: 0.6,
                    boxShadow: "0px 6px 14px #36408D08",
                    fontSize: "14px",
                    color: "#1D1D1D",
                    textAlign: 'left',
                  }}
                  inputProps={{
                    style: {
                      fontFamily: "Mundial, sans-serif",
                      fontSize: "14px",
                    },
                  }}
                  SelectDisplayProps={{
                    style: {
                      fontFamily: "Mundial, sans-serif",
                      fontSize: "14px",
                    },
                  }}
                >
                  <MenuItem value="" disabled>
                    Select Service Type...
                  </MenuItem>
                  <MenuItem value="Sales">Sales</MenuItem>
                  <MenuItem value="Branding">Branding</MenuItem>
                  <MenuItem value="Mentoring">Mentoring</MenuItem>
                </Select>
              )}
            />
            <FormHelperText error={!!errors.serviceType}>{errors.serviceType}</FormHelperText>
          </FormControl>

          {/* Dynamic Fields */}
          {serviceType === 'Sales' && (
            <>
              <FormControl fullWidth sx={{ marginBottom: 2 }}>
                <InputLabel  shrink sx={{ ml: -1 }}>Property Interested</InputLabel>
                <Controller
                  name="propertyInterested"
                  control={control}
                  render={({ field }) => (
                    <CustomInput
                      placeholder="Enter property..."
                      {...field}
                      error={!!errors.propertyInterested}
                    />
                  )}
                />
                <FormHelperText error>{errors.propertyInterested}</FormHelperText>
              </FormControl>
              <FormControl fullWidth sx={{ marginBottom: 2 }}>
                <InputLabel  shrink sx={{ ml: -1 }}>Budget Range</InputLabel>
                <Controller
                  name="budgetRange"
                  control={control}
                  render={({ field }) => (
                    <CustomInput
                      placeholder="Enter budget range..."
                      {...field}
                      error={!!errors.budgetRange}
                    />
                  )}
                />
                <FormHelperText error>{errors.budgetRange}</FormHelperText>
              </FormControl>
              <FormControl fullWidth sx={{ marginBottom: 2 }}>
                <InputLabel  shrink sx={{ ml: -1 }}>Location Preference</InputLabel>
                <Controller
                  name="locationPreference"
                  control={control}
                  render={({ field }) => (
                    <CustomInput
                      placeholder="Enter location preference..."
                      {...field}
                      error={!!errors.locationPreference}
                    />
                  )}
                />
                <FormHelperText error>{errors.locationPreference}</FormHelperText>
              </FormControl>
              <FormControl fullWidth sx={{ marginBottom: 2 }}>
                <InputLabel  shrink sx={{ ml: -1 }}>Timeline to Buy</InputLabel>
                <Controller
                  name="timelineToBuy"
                  control={control}
                  render={({ field }) => (
                    <CustomInput
                      placeholder="Enter timeline to buy..."
                      {...field}
                      error={!!errors.timelineToBuy}
                    />
                  )}
                />
                <FormHelperText error>{errors.timelineToBuy}</FormHelperText>
              </FormControl>
            </>
          )}

          {serviceType === 'Branding' && (
            <>
              <FormControl fullWidth sx={{ marginBottom: 2 }}>
                <InputLabel  shrink sx={{ ml: -1 }}>Business Name</InputLabel>
                <Controller
                  name="businessName"
                  control={control}
                  render={({ field }) => (
                    <CustomInput
                      placeholder="Enter business name..."
                      {...field}
                      error={!!errors.businessName}
                    />
                  )}
                />
                <FormHelperText error>{errors.businessName}</FormHelperText>
              </FormControl>
              <FormControl fullWidth sx={{ marginBottom: 2 }}>
                <InputLabel  shrink sx={{ ml: -1 }}>Industry</InputLabel>
                <Controller
                  name="industry"
                  control={control}
                  render={({ field }) => (
                    <CustomInput
                      placeholder="Enter industry..."
                      {...field}
                      error={!!errors.industry}
                    />
                  )}
                />
                <FormHelperText error>{errors.industry}</FormHelperText>
              </FormControl>
              <FormControl fullWidth sx={{ marginBottom: 2 }}>
                <InputLabel  shrink sx={{ ml: -1 }}>Current Challenges</InputLabel>
                <Controller
                  name="currentChallenges"
                  control={control}
                  render={({ field }) => (
                    <CustomInput
                      placeholder="Enter current challenges..."
                      {...field}
                      error={!!errors.currentChallenges}
                    />
                  )}
                />
                <FormHelperText error>{errors.currentChallenges}</FormHelperText>
              </FormControl>
              <FormControl fullWidth sx={{ marginBottom: 2 }}>
                <InputLabel  shrink sx={{ ml: -1 }}>Looking For</InputLabel>
                <Controller
                  name="lookingFor"
                  control={control}
                  render={({ field }) => (
                    <Select
                      multiple
                      {...field}
                      value={field.value || []} // Ensure value is always an array
                      renderValue={(selected) => selected.join(', ')}
                    >
                      <MenuItem value="Option1">
                        <Checkbox checked={field.value?.includes('Option1')} />
                        <ListItemText primary="Option1" />
                      </MenuItem>
                      <MenuItem value="Option2">
                        <Checkbox checked={field.value?.includes('Option2')} />
                        <ListItemText primary="Option2" />
                      </MenuItem>
                    </Select>
                  )}
                />
                <FormHelperText error>{errors.lookingFor}</FormHelperText>
              </FormControl>
            </>
          )}

          {serviceType === 'Mentoring' && (
            <>
              <FormControl fullWidth sx={{ marginBottom: 2 }}>
                <InputLabel  shrink sx={{ ml: -1 }}>Profession</InputLabel>
                <Controller
                  name="profession"
                  control={control}
                  render={({ field }) => (
                    <CustomInput
                      placeholder="Enter profession..."
                      {...field}
                      error={!!errors.profession}
                    />
                  )}
                />
                <FormHelperText error>{errors.profession}</FormHelperText>
              </FormControl>
              <FormControl fullWidth sx={{ marginBottom: 2 }}>
                <InputLabel  shrink sx={{ ml: -1 }}>Goal of Mentorship</InputLabel>
                <Controller
                  name="goalOfMentorship"
                  control={control}
                  render={({ field }) => (
                    <CustomInput
                      placeholder="Enter goal of mentorship..."
                      {...field}
                      error={!!errors.goalOfMentorship}
                    />
                  )}
                />
                <FormHelperText error>{errors.goalOfMentorship}</FormHelperText>
              </FormControl>
              <FormControl fullWidth sx={{ marginBottom: 2 }}>
                <InputLabel  shrink sx={{ ml: -1 }}>Preferred Mode</InputLabel>
                <Controller
                  name="preferredMode"
                  control={control}
                  render={({ field }) => (
                    <Select {...field}>
                      <MenuItem value="Call">Call</MenuItem>
                      <MenuItem value="Video">Video</MenuItem>
                      <MenuItem value="In-person">In-person</MenuItem>
                    </Select>
                  )}
                />
                <FormHelperText error>{errors.preferredMode}</FormHelperText>
              </FormControl>
              <FormControl fullWidth sx={{ marginBottom: 2 }}>
                <InputLabel  shrink sx={{ ml: -1 }}>Availability</InputLabel>
                <Controller
                  name="availability"
                  control={control}
                  render={({ field }) => (
                    <Select {...field}>
                      <MenuItem value="Weekdays">Weekdays</MenuItem>
                      <MenuItem value="Weekends">Weekends</MenuItem>
                      <MenuItem value="Evenings">Evenings</MenuItem>
                    </Select>
                  )}
                />
                <FormHelperText error>{errors.availability}</FormHelperText>
              </FormControl>
            </>
          )}

          {/* Submit Button */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={isLoading}
              sx={{
                textTransform: 'none',
                fontSize: '14px',
                padding: '10px 20px',
              }}
            >
              {isLoading ? 'Submitting...' : getButtonText()}
            </Button>
          </Box>
        </Box>
      </form>
    </Box>
  );
};

export default RajivForm;
